import React, { useState, useEffect, useContext, useRef } from "react";
import "../../styling/GalleryTab.css";
import "../../styling/ClientPortal.css";
import axios from "axios";
import { EventDataContext } from "../EventDataContext";
import { ImUpload } from "react-icons/im";
import { useSelector } from "react-redux";

const itemsPerPage = 12;

const GalleryTab = ({ brandingColours }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { EventData } = useContext(EventDataContext);
  const [images, setImages] = useState([]);
  const eventId = EventData._id;

  const [selectedImages, setSelectedImages] = useState([]);

  const user = useSelector((state) => state.auth.user);
  const businessId = user?.businessId;

  const folderInputRef = useRef(null);

  const fetchImages = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/galleryImages/${eventId}`,
        { withCredentials: true }
      );
      
      const fetchedImages = Array.isArray(res.data.images) ? res.data.images : [];
      setImages(fetchedImages);

      const newTotalPages = Math.ceil(fetchedImages.length / itemsPerPage);
      if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages > 0 ? newTotalPages : 1);
      }
      
    } catch (err) {
      console.error("Error fetching images:", err);
      setImages([]);
    }
  };

  useEffect(() => {
    if (eventId) {
        fetchImages();
    }
  }, [eventId]); 

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentImages = images.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(images.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const [imageCount, setImageCount] = useState(null);

  useEffect(() => {
    const fetchImageCount = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/business/image-count/${businessId}`,
          {
            withCredentials: true,
          }
        );

        setImageCount(response.data.totalImages);
      } catch (error) {
        console.error(
          "Error fetching image count:",
          error.response?.data || error.message
        );
      }
    };

    if (businessId) {
      fetchImageCount();
      fetchPlan();
    }
  }, [businessId]);

  const [plan, setPlan] = useState(null);

  const fetchPlan = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/business-user/subscription`,
        {
          withCredentials: true,
        }
      );
      setPlan(res.data.subscription.planName);
    } catch (error) {
      console.error("Error fetching subscription or plan:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;

    if (!files || files.length === 0) return;

    const incomingCount = files.length;
    const currentCount = typeof imageCount === 'number' ? imageCount : 0;
    const totalIfUploaded = currentCount + incomingCount;

    // 1. Starter Limit (1,500)
    if (plan === "Starter" && totalIfUploaded > 1500) {
      alert(
        `The Starter plan allows a maximum of 1,500 images across all events. You currently have ${currentCount}. Please upgrade to Essential or Bloom to upload more.`
      );
      return;
    }

    // 2. Essential Limit (5,000)
    if (plan === "Essential" && totalIfUploaded > 5000) {
      alert(
        `The Essential plan allows a maximum of 5,000 images across all events. You currently have ${currentCount}. Please upgrade to Bloom to upload more.`
      );
      return;
    }

    // 3. Bloom Limit (25,000)
    if (plan === "Bloom" && totalIfUploaded > 25000) {
      alert(
        `The Bloom plan allows a maximum of 25,000 images across all events. You currently have ${currentCount}.`
      );
      return;
    }

    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/uploadImages/${eventId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );
      alert("Images uploaded successfully");
      // Assuming fetchImages() updates the gallery state
      if (typeof fetchImages === 'function') {
        fetchImages(); 
      }
      
      // Also update the local image count immediately without reloading the page
      setImageCount(totalIfUploaded); 
      
    } catch (err) {
      console.error("Error uploading files:", err);
      alert(err.response?.data?.message || "An error occurred during upload. Please try again.");
    }
  };

  const downloadAllImages = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/event/${eventId}/download-images-zip`,
        {
          responseType: "blob",
          withCredentials: true,
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `event-${eventId}-images.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    // --- FIX: Added missing braces for the catch block ---
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      alert("Failed to download images as ZIP.");
    }
  };

  // --- MODIFIED: This now stores originalUrl strings ---
  const handleImageSelect = (imageUrl) => {
    setSelectedImages((prevSelected) => {
      if (prevSelected.includes(imageUrl)) {
        return prevSelected.filter((url) => url !== imageUrl);
      } else {
        return [...prevSelected, imageUrl];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedImages.length === 0) {
      alert("No images selected.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedImages.length} selected images? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/galleryImages/delete-multiple`,
        {
          // --- NOTE: 'imageIds' now contains an array of originalUrl strings ---
          data: { imageIds: selectedImages, eventId: eventId },
          withCredentials: true,
        }
      );

      alert("Images deleted successfully");
      setSelectedImages([]); 
      fetchImages(); 
      
    } catch (err) {
      console.error("Error deleting images:", err);
      alert("An error occurred while deleting images. Please try again.");
    }
  };

  return (
    <div className="gallery-container">
      <div
        className="image-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "16px",
        }}
      >
        {/* --- Upload Box (Unchanged) --- */}
        <div
          className="image-item"
          style={{
            aspectRatio: "1 / 1",
            width: "100%",
            height: "270px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: "2px dashed #ccc",
            borderRadius: "8px",
            textAlign: "center",
            padding: "8px",
            boxSizing: "border-box",
          }}
        >
          <ImUpload style={{ fontSize: "24px", marginBottom: "8px", color: "#666" }} />
          
          <label htmlFor="file-upload" style={{ cursor: 'pointer', color: '#2A1980', textDecoration: 'underline', fontSize: '14px' }}>
            Upload Files
          </label>

          {/* <p style={{ margin: '8px 0', fontSize: '12px', color: '#666' }}>or</p> */}
          
          {/* <button 
            onClick={() => folderInputRef.current && folderInputRef.current.click()}
            style={{
              cursor: 'pointer',
              color: '#007bff',
              textDecoration: 'underline',
              fontSize: '14px',
              border: 'none',
              background: 'none',
              padding: 0
            }}
          >
            Upload Folder
          </button> */}

          <input
            type="file"
            id="file-upload"
            name="files"
            multiple
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />

          <input
            type="file"
            id="folder-upload"
            webkitdirectory="true" 
            style={{ display: "none" }}
            onChange={handleFileUpload}
            ref={folderInputRef} 
          />
        </div>
        {/* --- End Upload Box --- */}


        {currentImages.map((image, index) => (
          <div
            // --- FIX 2: Use originalUrl as the unique key ---
            key={image.originalUrl || index}
            className="image-item"
            style={{
              aspectRatio: "1 / 1",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: 'relative', 
            }}
          >
            <input
              type="checkbox"
              // --- FIX 2: Check selection using originalUrl ---
              checked={selectedImages.includes(image.originalUrl)}
              // --- FIX 2: Handle selection using originalUrl ---
              onChange={() => handleImageSelect(image.originalUrl)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '20px',
                height: '20px',
                zIndex: 10,
                cursor: 'pointer',
              }}
            />
            
            <img
              src={image.thumbnailUrl}
              alt={`Image ${index}`}
              className="img-fluid"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>
        ))}
      </div>

      <div className="pagination" style={{ marginTop: "16px", textAlign: "center" }}>
        <div style={{ marginBottom: "16px" }}>
          <button
            style={{
              marginTop: "5px",
              width: "auto", 
              padding: "0 15px", 
              height: "35px",
              background: "#2A1980", 
              color: "#FFFFFF", 
              fontSize: "12px",
              border: "hidden",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={downloadAllImages}
          >
            <ImUpload /> Download All
          </button>

          {selectedImages.length > 0 && (
            <button
              style={{
                marginTop: "5px",
                width: "auto", 
                padding: "0 15px", 
                height: "35px",
                background: "#FFFFFF", 
                color: "#2A1980",
                fontSize: "12px",
                border: "hidden",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "10px", 
                border: "1px solid #d4d4d4"
              }}
              onClick={handleDeleteSelected}
            >
              Delete Selected ({selectedImages.length})
            </button>
          )}
        </div>

        {/* Pagination Numbers */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: "2px", flexWrap: "wrap" }}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <div
              key={index}
              className="page-item"
              style={{
                color: "#2A1980",
                backgroundColor: '#FFFFFF',
                cursor: "pointer",
                borderRadius: "4px",
                border: "1px solid",
                height: "17px",
                margin: "4px",
              }}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GalleryTab;